import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useToast } from '../hooks/useToast'

interface RoutingCriteria {
  departmentId: string
  priority: string
  estimatedHours?: number
  tags?: string[]
  staffOrganizationId: string
}

interface StaffCandidate {
  userId: string
  firstName: string
  lastName: string
  email: string
  role: string
  expertiseLevel: number
  maxConcurrentTickets: number
  currentTicketCount: number
  availabilityScore: number
  matchScore: number
}

interface Department {
  id: string
  name: string
}

interface StaffOrganization {
  id: string
  name: string
}

const SmartRouting: React.FC = () => {
  const { showSuccess, showError } = useToast()
  const [departments, setDepartments] = useState<Department[]>([])
  const [staffOrganizations, setStaffOrganizations] = useState<StaffOrganization[]>([])
  const [loading, setLoading] = useState(false)
  const [recommendations, setRecommendations] = useState<StaffCandidate[]>([])
  const [bestMatch, setBestMatch] = useState<StaffCandidate | null>(null)
  const [testResults, setTestResults] = useState<any>(null)

  const [criteria, setCriteria] = useState<RoutingCriteria>({
    departmentId: '',
    priority: 'medium',
    estimatedHours: 4,
    tags: [],
    staffOrganizationId: ''
  })

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ]

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [departmentsRes, organizationsRes] = await Promise.all([
        axios.get('/api/tickets/departments'),
        axios.get('/api/organizations')
      ])
      
      setDepartments(departmentsRes.data.data)
      setStaffOrganizations(organizationsRes.data.data)
    } catch (error) {
      console.error('Error fetching data:', error)
      showError('Failed to fetch data')
    }
  }

  const handleTestRouting = async () => {
    if (!criteria.departmentId || !criteria.staffOrganizationId) {
      showError('Please select both department and staff organization')
      return
    }

    setLoading(true)
    try {
      const response = await axios.post('/api/smart-routing/test', criteria)
      setTestResults(response.data.data)
      setBestMatch(response.data.data.bestMatch)
      setRecommendations(response.data.data.recommendations)
      showSuccess('Routing test completed successfully')
    } catch (error: any) {
      console.error('Error testing routing:', error)
      showError(error.response?.data?.error || 'Failed to test routing')
    } finally {
      setLoading(false)
    }
  }

  const handleGetRecommendations = async () => {
    if (!criteria.departmentId || !criteria.staffOrganizationId) {
      showError('Please select both department and staff organization')
      return
    }

    setLoading(true)
    try {
      const response = await axios.post('/api/smart-routing/recommendations', criteria)
      setRecommendations(response.data.data)
      showSuccess('Recommendations retrieved successfully')
    } catch (error: any) {
      console.error('Error getting recommendations:', error)
      showError(error.response?.data?.error || 'Failed to get recommendations')
    } finally {
      setLoading(false)
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'LEAD': return 'bg-blue-100 text-blue-800'
      case 'SPECIALIST': return 'bg-purple-100 text-purple-800'
      case 'MEMBER': return 'bg-green-100 text-green-800'
      case 'BACKUP': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Smart Ticket Routing</h1>
          <p className="mt-2 text-sm text-gray-700">
            Test and manage intelligent ticket routing based on staff expertise and workload
          </p>
        </div>
      </div>

      {/* Test Criteria Form */}
      <div className="mt-8 card">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Test Routing Criteria</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department
            </label>
            <select
              value={criteria.departmentId}
              onChange={(e) => setCriteria({ ...criteria, departmentId: e.target.value })}
              className="input-field"
            >
              <option value="">Select department</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Staff Organization
            </label>
            <select
              value={criteria.staffOrganizationId}
              onChange={(e) => setCriteria({ ...criteria, staffOrganizationId: e.target.value })}
              className="input-field"
            >
              <option value="">Select organization</option>
              {staffOrganizations.map(org => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={criteria.priority}
              onChange={(e) => setCriteria({ ...criteria, priority: e.target.value })}
              className="input-field"
            >
              {priorities.map(priority => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estimated Hours
            </label>
            <input
              type="number"
              min="1"
              max="40"
              value={criteria.estimatedHours}
              onChange={(e) => setCriteria({ ...criteria, estimatedHours: parseInt(e.target.value) || 4 })}
              className="input-field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={criteria.tags?.join(', ') || ''}
              onChange={(e) => setCriteria({ 
                ...criteria, 
                tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
              })}
              className="input-field"
              placeholder="technical, urgent, client-request"
            />
          </div>
        </div>

        <div className="mt-6 flex space-x-3">
          <button
            onClick={handleTestRouting}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Testing...' : 'Test Smart Routing'}
          </button>
          
          <button
            onClick={handleGetRecommendations}
            disabled={loading}
            className="btn-secondary"
          >
            {loading ? 'Loading...' : 'Get Recommendations'}
          </button>
        </div>
      </div>

      {/* Best Match Display */}
      {bestMatch && (
        <div className="mt-8 card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Best Match</h2>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-green-900">
                  {bestMatch.firstName} {bestMatch.lastName}
                </h3>
                <p className="text-green-700">{bestMatch.email}</p>
                <div className="flex space-x-2 mt-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(bestMatch.role)}`}>
                    {bestMatch.role}
                  </span>
                  <span className="text-sm text-green-600">
                    Expertise Level {bestMatch.expertiseLevel}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600">
                  {Math.round(bestMatch.availabilityScore + bestMatch.matchScore)}
                </div>
                <div className="text-sm text-green-600">Total Score</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-700">Current Tickets:</span>
                <span className="ml-2 font-medium">{bestMatch.currentTicketCount}/{bestMatch.maxConcurrentTickets}</span>
              </div>
              <div>
                <span className="text-green-700">Availability:</span>
                <span className="ml-2 font-medium">{Math.round(bestMatch.availabilityScore)}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations Display */}
      {recommendations.length > 0 && (
        <div className="mt-8 card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Top Recommendations</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Staff Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role & Expertise
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workload
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scores
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recommendations.map((candidate, index) => (
                  <tr key={candidate.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {index + 1}. {candidate.firstName} {candidate.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{candidate.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(candidate.role)}`}>
                          {candidate.role}
                        </span>
                        <span className="text-sm text-gray-600">
                          Level {candidate.expertiseLevel}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {candidate.currentTicketCount}/{candidate.maxConcurrentTickets} tickets
                      </div>
                      <div className="text-sm text-gray-500">
                        {Math.round(candidate.availabilityScore)}% available
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div>Match: {Math.round(candidate.matchScore)}</div>
                        <div>Total: {Math.round(candidate.availabilityScore + candidate.matchScore)}</div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Test Results */}
      {testResults && (
        <div className="mt-8 card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Test Results</h2>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Routing Criteria Used:</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Department:</span>
                <span className="ml-2 font-medium">
                  {departments.find(d => d.id === testResults.criteria.departmentId)?.name}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Priority:</span>
                <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(testResults.criteria.priority)}`}>
                  {testResults.criteria.priority}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Est. Hours:</span>
                <span className="ml-2 font-medium">{testResults.criteria.estimatedHours}</span>
              </div>
              {testResults.criteria.tags && testResults.criteria.tags.length > 0 && (
                <div className="col-span-2 md:col-span-3">
                  <span className="text-gray-600">Tags:</span>
                  <span className="ml-2 font-medium">{testResults.criteria.tags.join(', ')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SmartRouting
